import { FC, ReactNode, Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, useParams, Routes } from 'react-router-dom';
import { LoadingSpinner } from 'View/Common';
import { linkHandler } from 'utils';
import { useRouteData } from 'View/Application/RouteDataProvider/RouteDataProvider';
import { TRoute } from 'config/routes';
import NotFound from 'views/NotFound';

type TCustomContentRendererProps = TRoute & {
  routeKey: string;
  path: string;
};

const CustomContentRenderer: FC<TCustomContentRendererProps> = ({ Component, routeKey, ...props }) => {
  const { currentRoute, setCurrentRoute } = useRouteData();
  const params = useParams();

  useEffect(() => {
    if (
      currentRoute?.key !== routeKey ||
      currentRoute?.path !== props.path ||
      JSON.stringify(currentRoute?.params || {}) !== JSON.stringify(params)
    ) {
      setCurrentRoute({
        ...props,
        key: routeKey,
        params,
        Component,
      });
    }
  }, [params, currentRoute]);

  if (currentRoute?.redirectTo) {
    return <Navigate to={currentRoute.redirectTo} replace />;
  }

  //@ts-ignore
  return <Component {...props} routeKey={routeKey} />;
};

export const Router: FC<{ children: ReactNode }> = ({ children }) => {
  const renderFallback = () => (
    <div style={{ minHeight: 600 }}>
      <LoadingSpinner />
    </div>
  );

  const routes = useMemo(() => {
    const _routes = linkHandler.getAll();
    return Object.keys(_routes).map((routeKey) => _routes[routeKey]);
  }, []);

  const redirects = useMemo(() => routes.filter((route) => !!route.redirects?.length), [routes]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/*" element={<NotFound />} />
        {routes
          .filter(({ Component }) => !!Component)
          .map(({ path, key, Component, ...rest }, index) => {
            return (
              <Route
                path={path}
                key={`route-${key}-${index}`}
                element={
                  <Suspense fallback={renderFallback()}>
                    <CustomContentRenderer routeKey={key} path={path} Component={Component} {...rest} />
                  </Suspense>
                }
              />
            );
          })}
        {redirects.reduce(
          (acc, { redirects, path, key }, index) =>
            acc.concat(
              // @ts-ignore
              redirects.map((from, rx) => (
                <Route path={from} element={<Navigate to={path} replace />} key={`redirect-entry-${key}-${index}-${rx}`} />
              )),
            ),
          [],
        )}
      </Routes>
      {children}
    </BrowserRouter>
  );
};
